/* global Sentry */
import {
  panel,
  refinementList,
  hierarchicalMenu,
  rangeInput,
  numericMenu,
  toggleRefinement,
  clearRefinements
} from 'instantsearch.js/es/widgets'

import {
  sortStringsAsNumbers,
  sortArtSizes,
  withArtSizeRange,
  modalOrList,
  excludedAttributesForClearRefinements,
  removeUnknowns,
  noOfSeatersOrFour,
  removeSeatersMoreThanFour,
  customPriceRangeWidget
} from '../../utils/search/filters'
import { facetValueRetrievalLimit } from '../../utils/search/facetLimits'
import { hierarchyToPath } from '../../utils/search/categoryPaths'

import { titleCase } from '../../utils/stringHelpers'
import { t } from '../../utils/search/translations'
import { refinementListItemTemplate } from './refinementTemplates'
import { shouldHide } from '../../libs/search/filters/shouldHide'
import { withFixedBounds } from '../../libs/search/filters/fixedBounds'
import { getPriceAttributeName } from '../../utils/search/helpers'
import { recommendedFilters } from './recommendedFilterWidgets'

function getDesktopPanel (title, attribute = null) {
  return panel({
    templates: {
      header: `<h5>${title}</h5>`
    },
    cssClasses: {
      root: `facet-name-${attribute}`
    },
    collapsed: (options) => collapsePanel(options, attribute),
    hidden (options) {
      return shouldHide(attribute, options)
    }
  })
}

// The most-used filters (set per user type on the filter partial's root div)
// start expanded so they can be used without opening each group first
function defaultExpandedAttributes () {
  const filtersRoot = document.querySelector('[data-default-expanded-filters]')
  if (!filtersRoot) return []

  return filtersRoot.dataset.defaultExpandedFilters.split(' ')
}

function collapsePanel (options, attribute) {
  // Collapse all panels by default except the per-user-type defaults, those with active refinements or in URL
  if (defaultExpandedAttributes().includes(attribute)) return false
  if (window.location.search.includes(attribute)) return false

  const refinements = options.state && options.state.disjunctiveFacetsRefinements
  const hasActiveRefinements = refinements && refinements[attribute] && refinements[attribute].length > 0
  if (hasActiveRefinements) return false

  return true
}

// We start by showing 5 filters by default. When clicking Show More we can expand a list up to 100
// It won't show a scrollbar if we just have about 20 items, but will start showing it from that point onward
// so we can scroll through the list of 100
// If facet can have 100+ filters, it opens a modal instead where we can scroll and search

function getShowMoreSettings () {
  return {
    showMore: true,
    showMoreLimit: facetValueRetrievalLimit(),
    limit: 5
  }
}

// Removes the `p` query if found.
function removeQueriesFromURL (data) {
  const location = new URL(data.url)
  const searchParams = new URLSearchParams(location.search)

  if (searchParams.has('p')) {
    searchParams.delete('p')
    location.search = searchParams.toString()
  }

  const searchParamIsEmptyQuery = searchParams.has('q') && location.search === '?q='

  if (searchParamIsEmptyQuery) {
    searchParams.delete('q')
    location.search = searchParams.toString()
  }

  return location.toString()
}

// For the refined menu items Algolia assigns the url to move back a category
// however for SEO purposes we want to show the matching URL
function getRelevantUrl (data) {
  const location = new URL(data.url)

  if (data.value) {
    const path = hierarchyToPath(data.value)
    return location.origin + '/' + path
  } else {
    if (window.Sentry) {
      Sentry.captureMessage(`Hierarchy menu item doesn't have a value: ${data.value}`)
    }

    return removeQueriesFromURL(data)
  }
}

function categoryLink (data) {
  if (data.isRefined) {
    return getRelevantUrl(data)
  } else {
    return removeQueriesFromURL(data)
  }
}

function getPanelWithRefinementList (title, attribute) { return getDesktopPanel(title, attribute)(refinementList) }
function getPanelWithRangeInput (title, attribute) { return getDesktopPanel(title, attribute)(withFixedBounds(rangeInput)) }

// Fixed bounds, not the result set's facets_stats: connectRange DISCARDS an
// out-of-range refinement rather than clamping it, so a shopper's numbers are
// lost silently at the edges. Supersets of every real value, kept out of the
// refinement itself by withFixedBounds.
// See docs/search/typesense-relevance.md.
const DIMENSION_BOUNDS = { min: 0, max: 100000 }
const PRICE_BOUNDS = { min: 0, max: 10000000 }

const designerFilter = getPanelWithRefinementList(t('filter_designer'), 'designed_by_name')({
  container: '#designer',
  attribute: 'designed_by_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  searchable: true,
  searchablePlaceholder: t('search_by_designer'),
  ...getShowMoreSettings(),
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'designed_by_name')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  }
})

const periodFilter = getPanelWithRefinementList(t('filter_period'), 'year_of_manufacture')({
  container: '#period-filter',
  attribute: 'year_of_manufacture',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  ...getShowMoreSettings(),
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'year_of_manufacture')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  },
  transformItems (items) {
    return removeUnknowns(items)
  }
})

const brandFilter = getPanelWithRefinementList(t('filter_brand'), 'brand_name')({
  container: '#brand-filter',
  attribute: 'brand_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  searchable: true,
  searchablePlaceholder: t('search_by_brand'),
  showMore: true,
  showMoreLimit: facetValueRetrievalLimit(),
  // 1 item will be hidden during transformItems ('unknown')
  limit: 6,
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'brand_name')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  },
  transformItems (items) {
    return removeUnknowns(items)
  }
})

const conditionFilter = getPanelWithRefinementList(t('filter_condition'), 'age_group_name')({
  container: '#age-group',
  attribute: 'age_group_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  ...getShowMoreSettings()
})

const priceFilter = withFixedBounds(customPriceRangeWidget)({
  container: '#price',
  attribute: getPriceAttributeName(),
  ...PRICE_BOUNDS,
  cssClasses: {
    root: 'price-ranges-root'
  },
  // These are hardcoded so look at customPriceRangeWidget render and make dynamic if needed
  templates: {
    separatorText: t('price_separator'),
    submitText: t('apply')
  }
})

const widthFilter = getPanelWithRangeInput(t('filter_width'), 'width_float')({
  container: '#width',
  attribute: 'width_float',
  ...DIMENSION_BOUNDS,
  cssClasses: {
    root: 'width-ranges-root',
    list: 'width-range-list'
  },
  templates: {
    submitText: t('apply')
  }
})

const heightFilter = getPanelWithRangeInput(t('filter_height'), 'height_float')({
  container: '#height',
  attribute: 'height_float',
  ...DIMENSION_BOUNDS,
  cssClasses: {
    root: 'height-ranges-root',
    list: 'height-range-list'
  },
  templates: {
    submitText: t('apply')
  }
})

const depthFilter = getPanelWithRangeInput(t('filter_depth'), 'depth_float')({
  container: '#depth',
  attribute: 'depth_float',
  ...DIMENSION_BOUNDS,
  cssClasses: {
    root: 'depth-ranges-root',
    list: 'depth-range-list'
  },
  templates: {
    submitText: t('apply')
  }
})

const styleFilter = getPanelWithRefinementList(t('filter_style'), 'vibe_names')({
  container: '#vibe',
  attribute: 'vibe_names',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'vibe_names')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  },
  ...getShowMoreSettings()
})

const colourFilter = getPanelWithRefinementList(t('filter_colours'), 'color_name')({
  container: '#color',
  attribute: 'color_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'color_name')
    },
    item (hit) {
      return refinementListItemTemplate(hit, 'colour')
    }
  },
  cssClasses: {
    item: 'ais-RefinementList-item__bordered'
  },
  ...getShowMoreSettings()
})

const materialFilter = getPanelWithRefinementList(t('filter_main_material'), 'main_material_name')({
  container: '#main-material',
  attribute: 'main_material_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  searchable: true,
  searchablePlaceholder: t('search_by_material'),
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'main_material_name')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  },
  ...getShowMoreSettings()
})

const locationFilter = getPanelWithRefinementList(t('filter_shipped_from'), 'country_from_address')({
  container: '#location',
  attribute: 'country_from_address',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'country_from_address')
    },
    item (hit) {
      return refinementListItemTemplate(hit, 'flag')
    }
  },
  cssClasses: {
    item: 'ais-RefinementList-item__bordered'
  },
  ...getShowMoreSettings()
})

const locationCityFilter = getPanelWithRefinementList(t('filter_city'), 'address_city')({
  container: '#location-city',
  attribute: 'address_city',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  searchable: true,
  searchablePlaceholder: t('search_by_city'),
  ...getShowMoreSettings(),
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'address_city')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  },
  transformItems (items) {
    return items.map(item => ({
      ...item,
      highlighted: titleCase(item.label)
    }))
  }
})

const londonDeliveryFilter = toggleRefinement({
  container: '#free-london-delivery',
  attribute: 'free_london_delivery_for_algolia',
  templates: {
    labelText: t('free_london_delivery')
  },
  values: {
    on: 'true'
  }
})

const freeUkDeliveryFilter = toggleRefinement({
  container: '#free-uk-delivery',
  attribute: 'free_uk_delivery_for_algolia',
  templates: {
    labelText: t('free_uk_delivery')
  },
  values: {
    on: 'true'
  }
})

const freeCollectionFilter = toggleRefinement({
  container: '#free-collection',
  attribute: 'free_collection',
  templates: {
    labelText: t('free_collection')
  },
  values: {
    on: 'true'
  }
})

const topSellerFilter = toggleRefinement({
  container: '#top-seller',
  attribute: 'top_score_seller',
  templates: {
    labelText: t('top_sellers_only')
  },
  values: {
    on: 'true'
  }
})

const professionalSellerFilter = toggleRefinement({
  container: '#professional-seller',
  attribute: 'professional_seller',
  templates: {
    labelText: t('professional_sellers_only')
  },
  values: {
    on: 'true'
  }
})

const tradePriceOnlyFilter = toggleRefinement({
  container: '#trade-filter',
  attribute: 'trade_price_and_trade_price_under_sale_price',
  templates: {
    labelText: t('trade_price_only')
  },
  values: {
    on: 'true'
  }
})

const saleFilter = toggleRefinement({
  container: '#sale-filter',
  attribute: 'sale',
  templates: {
    labelText: t('on_sale')
  },
  values: {
    on: 'true'
  }
})

const reupholsteredFilter = toggleRefinement({
  container: '#professionally-reupholstered-filter',
  attribute: 'professionally_reupholstered',
  templates: {
    labelText: t('professionally_reupholstered')
  },
  values: {
    on: 'true'
  }
})

const reupholsteryAvailableFilter = toggleRefinement({
  container: '#reupholstery-available-filter',
  attribute: 'reupholstery_available',
  templates: {
    labelText: t('reupholstery_available')
  },
  values: {
    on: 'true'
  }
})

const refurbishedFilter = toggleRefinement({
  container: '#professionally-refurbished-filter',
  attribute: 'professionally_refurbished',
  templates: {
    labelText: t('professionally_refurbished')
  },
  values: {
    on: 'true'
  }
})

const cleanedFilter = toggleRefinement({
  container: '#professionally-cleaned-filter',
  attribute: 'professionally_cleaned',
  templates: {
    labelText: t('professionally_cleaned')
  },
  values: {
    on: 'true'
  }
})

const authenticityFilter = toggleRefinement({
  container: '#certificate-of-authenticity-filter',
  attribute: 'certificate_of_authenticity',
  templates: {
    labelText: t('certificate_of_authenticity')
  },
  values: {
    on: 'true'
  }
})

const signedFilter = toggleRefinement({
  container: '#signed-by-artist-filter',
  attribute: 'signed_by_artist',
  templates: {
    labelText: t('signed_by_artist')
  },
  values: {
    on: 'true'
  }
})

const sustainablePackagingFilter = toggleRefinement({
  container: '#sustainable-packaging-filter',
  attribute: 'sustainable_packaging',
  templates: {
    labelText: t('sustainable_packaging')
  },
  values: {
    on: 'true'
  }
})

// Seasonal filter - DO NOT DELETE. To make it visible, uncomment below
// and add it in the "desktopFilterWidgets" array

const inTimeForChristmasFilter = toggleRefinement({
  container: '#in-time-for-christmas',
  attribute: 'in_time_for_christmas_for_algolia',
  templates: {
    labelText: t('in_time_for_christmas')
  },
  values: {
    on: 'true'
  }
})

const numberOfDoorsFilter = getPanelWithRefinementList(t('filter_number_of_doors'), 'number_of_doors')({
  container: '#number-of-doors',
  attribute: 'number_of_doors',
  sortBy: sortStringsAsNumbers,
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'number_of_doors')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  },
  ...getShowMoreSettings()
})

const numberOfDrawersFilter = getPanelWithRefinementList(t('filter_number_of_drawers'), 'number_of_drawers')({
  container: '#number-of-drawers',
  attribute: 'number_of_drawers',
  sortBy: sortStringsAsNumbers,
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'number_of_drawers')
    }
  },
  ...getShowMoreSettings()
})

const numberOfShelvesFilter = getPanelWithRefinementList(t('filter_number_of_shelves'), 'number_of_shelves')({
  container: '#number-of-shelves',
  attribute: 'number_of_shelves',
  sortBy: sortStringsAsNumbers,
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'number_of_shelves')
    }
  },
  ...getShowMoreSettings()
})

const numberOfSeatsFilter = getPanelWithRefinementList(t('filter_number_of_seats'), 'number_of_seats')({
  container: '#number-of-seats',
  attribute: 'number_of_seats',
  sortBy: sortStringsAsNumbers,
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'number_of_seats')
    }
  },
  transformItems (items) {
    return removeSeatersMoreThanFour(items).map(item => noOfSeatersOrFour(item))
  },
  ...getShowMoreSettings()
})

const featureNamesFilter = getPanelWithRefinementList(t('filter_feature'), 'feature_names')({
  container: '#feature-names',
  attribute: 'feature_names',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'feature_names')
    }
  },
  ...getShowMoreSettings()
})

const seatingShapeNamesFilter = getPanelWithRefinementList(t('filter_seating_shape'), 'seating_shape_names')({
  container: '#seating-shape-names',
  attribute: 'seating_shape_names',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'seating_shape_names')
    }
  },
  ...getShowMoreSettings()
})

const doorTypeFilter = getPanelWithRefinementList(t('filter_door_type'), 'door_type_name')({
  container: '#door-type',
  attribute: 'door_type_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'door_type_name')
    }
  },
  ...getShowMoreSettings()
})

const shapeFilter = getPanelWithRefinementList(t('filter_shape'), 'shape_name')({
  container: '#shape',
  attribute: 'shape_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'shape_name')
    },
    item (hit) {
      return refinementListItemTemplate(hit)
    }
  },
  ...getShowMoreSettings()
})

const orientationFilter = getPanelWithRefinementList(t('filter_orientation'), 'orientation_name')({
  container: '#orientation',
  attribute: 'orientation_name',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'orientation_name')
    }
  },
  ...getShowMoreSettings()
})

const artSizeFilter = getPanelWithRefinementList(t('filter_art_size'), 'art_size_name')({
  container: '#art-size',
  attribute: 'art_size_name',
  sortBy: sortArtSizes,
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'art_size_name')
    }
  },
  transformItems (items) {
    return items.map((item) => withArtSizeRange(item))
  },
  ...getShowMoreSettings()
})

const artSubjectNamesFilter = getPanelWithRefinementList(t('filter_art_subject'), 'art_subject_names')({
  container: '#art-subject-names',
  attribute: 'art_subject_names',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'art_subject_names')
    }
  },
  ...getShowMoreSettings()
})

const artisticMovementNamesFilter = getPanelWithRefinementList(t('filter_artistic_movement'), 'artistic_movement_names')({
  container: '#artistic-movement-names',
  attribute: 'artistic_movement_names',
  sortBy: ['count:desc', 'name:asc'],
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'artistic_movement_names')
    }
  },
  ...getShowMoreSettings()
})

const setOfFilter = getPanelWithRefinementList(t('filter_number_in_set'), 'set_of')({
  container: '#number-in-set',
  attribute: 'set_of',
  sortBy: sortStringsAsNumbers,
  operator: 'or',
  templates: {
    showMoreText (data) {
      return modalOrList(data.isShowingMore, 'set_of')
    }
  },
  transformItems (items) {
    return items.map(item => ({
      ...item,
      highlighted: 'Set of ' + item.label
    }))
  },
  ...getShowMoreSettings()
})

const salePercentagePanel = panel({
  templates: {
    header: `<h5>${t('discount_header')}</h5>`
  },
  collapsed: (options) => collapsePanel(options, 'sale_percentage_int')
})(numericMenu)

const salePercentageFilter = salePercentagePanel({
  container: '#sale-percentage',
  attribute: 'sale_percentage_int',
  items: [
    { label: t('discount_all') },
    { label: t('discount_10_20'), start: 10.0, end: 20.0 },
    { label: t('discount_20_30'), start: 20.0, end: 30.0 },
    { label: t('discount_30_50'), start: 30.0, end: 50.0 },
    { label: t('discount_over_50'), start: 50.0 }
  ]
})

const clearAll = clearRefinements({
  container: '.current-refinements__clear-all',
  templates: {
    resetLabel: t('clear_filters')
  },
  excludedAttributes: excludedAttributesForClearRefinements(),
  cssClasses: {
    disabledButton: 'hidden'
  }
})

const categoryHierarchyFilter = hierarchicalMenu({
  container: '#category-hierarchy-filter',
  attributes: [
    'category_hierarchy.lvl0',
    'category_hierarchy.lvl1',
    'category_hierarchy.lvl2',
    'category_hierarchy.lvl3'
  ],
  sortBy: ['isRefined', 'count:desc'],
  limit: 15,
  templates: {
    item (data) {
      return `
        <a class="${data.cssClasses.link}" href="${categoryLink(data)}">
          <span class="${data.cssClasses.label}">${data.label}</span>
        </a>
        <span class="${data.cssClasses.count}">
          ${data.count.toLocaleString()}
        </span>
      `
    }
  }
})

const desktopFilterWidgets = [
  designerFilter,
  periodFilter,
  brandFilter,
  conditionFilter,
  widthFilter,
  heightFilter,
  depthFilter,
  styleFilter,
  colourFilter,
  materialFilter,
  locationFilter,
  locationCityFilter,
  londonDeliveryFilter,
  freeUkDeliveryFilter,
  topSellerFilter,
  professionalSellerFilter,
  tradePriceOnlyFilter,
  saleFilter,
  reupholsteredFilter,
  reupholsteryAvailableFilter,
  refurbishedFilter,
  cleanedFilter,
  authenticityFilter,
  signedFilter,
  freeCollectionFilter,
  featureNamesFilter,
  seatingShapeNamesFilter,
  doorTypeFilter,
  shapeFilter,
  orientationFilter,
  artSizeFilter,
  artSubjectNamesFilter,
  artisticMovementNamesFilter,
  numberOfShelvesFilter,
  numberOfDrawersFilter,
  numberOfDoorsFilter,
  numberOfSeatsFilter,
  setOfFilter,
  salePercentageFilter, // Remove?
  clearAll,
  ...recommendedFilters('desktop')
]

// Load "In time for Christmas" filter if in the time range
const today = new Date()
const startDate = new Date('2025-11-28')
const endDate = new Date('2025-12-20')
const isTodayInRange = today >= startDate && today <= endDate

if (isTodayInRange) {
  desktopFilterWidgets.push(inTimeForChristmasFilter)
}

const declaredDateTimeStr = '2025-04-21 08:00'
const declaredDateTime = new Date(declaredDateTimeStr)
const currentDateTime = new Date()

if (currentDateTime > declaredDateTime) {
  desktopFilterWidgets.push(sustainablePackagingFilter)
}

const desktopPriceFilterWidgets = [
  priceFilter
]

// The category widget is registered separately in search.js: hierarchicalMenu by
// default, the multi-select tree behind :plp_multi_select_categories
export { desktopFilterWidgets, desktopPriceFilterWidgets, categoryHierarchyFilter }
